// src/components/store/AuthModal.tsx
"use client";

import React, { useState, useEffect, useCallback } from "react";
import { X, Mail, Lock, User, Phone, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { resolveClientSubdomain } from "@/lib/api";

interface AuthModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  defaultTab?: "login" | "register";
  subdomain?: string;
}

export default function AuthModal({
  isOpen: propIsOpen,
  onClose: propOnClose,
  defaultTab: propDefaultTab,
  subdomain: propSubdomain,
}: AuthModalProps) {
  const { login, isAuthModalOpen, closeAuthModal, authModalTab } = useAuth();

  const isOpen = propIsOpen !== undefined ? propIsOpen : isAuthModalOpen;
  const onClose = propOnClose || closeAuthModal;
  const effectiveDefaultTab = propDefaultTab || authModalTab || "login";

  const [tab, setTab] = useState<"login" | "register">(effectiveDefaultTab);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");

  const subdomain = resolveClientSubdomain(propSubdomain);

  useEffect(() => {
    setTab(effectiveDefaultTab);
    setError("");
    setSuccessMsg("");
  }, [effectiveDefaultTab, isOpen]);

  const saveSessionAndClose = useCallback(
    (data: any, fallbackEmail: string, fallbackName: string, fallbackPhone?: string) => {
      const access = data?.tokens?.access || data?.access || data?.token || "";
      const refresh = data?.tokens?.refresh || data?.refresh || "";

      const rawUser = data?.user || data?.customer || {};
      const resolvedEmail = (rawUser.email || data?.customer?.email || fallbackEmail || "")
        .trim()
        .toLowerCase();
      const resolvedFirstName =
        rawUser.first_name || data?.customer?.first_name || fallbackName.split(" ")[0] || "";
      const resolvedLastName =
        rawUser.last_name ||
        data?.customer?.last_name ||
        fallbackName.split(" ").slice(1).join(" ") ||
        "";
      const resolvedPhone = rawUser.phone || data?.customer?.phone || fallbackPhone || "";

      const userObj = {
        id: rawUser.id || data?.customer?.id || Date.now(),
        email: resolvedEmail,
        first_name: resolvedFirstName,
        last_name: resolvedLastName,
        name:
          rawUser.name ||
          `${resolvedFirstName} ${resolvedLastName}`.trim() ||
          resolvedEmail.split("@")[0],
        phone: resolvedPhone,
        role: "customer",
        subdomain: subdomain,
      };

      if (typeof window !== "undefined") {
        if (subdomain) {
          localStorage.setItem(`xylo_user_${subdomain}`, JSON.stringify(userObj));
          localStorage.setItem(`customer_info_${subdomain}`, JSON.stringify(userObj));
          if (access) localStorage.setItem(`xylo_token_${subdomain}`, access);
          if (refresh) localStorage.setItem(`xylo_refresh_${subdomain}`, refresh);
        }
        localStorage.setItem("xylo_user", JSON.stringify(userObj));
        localStorage.setItem("customer_info", JSON.stringify(userObj));
        if (access) {
          localStorage.setItem("access_token", access);
          localStorage.setItem("token", access);
        }
        if (refresh) {
          localStorage.setItem("refresh_token", refresh);
        }
      }

      login(access, userObj);

      setSuccessMsg(tab === "login" ? "সফলভাবে লগইন হয়েছে!" : "রেজিস্ট্রেশন সম্পন্ন হয়েছে!");
      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 400);
    },
    [login, onClose, subdomain, tab]
  );

  const completeGoogleBackendAuth = useCallback(
    async (userInfo: { email: string; name: string; first_name?: string; last_name?: string }) => {
      setError("");
      setGoogleLoading(true);

      try {
        const cleanEmail = userInfo.email.trim().toLowerCase();
        const fName = userInfo.first_name || userInfo.name?.split(" ")[0] || cleanEmail.split("@")[0];
        const lName = userInfo.last_name || userInfo.name?.split(" ").slice(1).join(" ") || "";

        const headers: Record<string, string> = {};
        if (subdomain) {
          headers["X-Tenant-Subdomain"] = subdomain;
        }

        const payload = {
          email: cleanEmail,
          name: userInfo.name || `${fName} ${lName}`.trim(),
          first_name: fName,
          last_name: lName,
          phone: "",
          subdomain,
        };

        let res;
        try {
          res = await api.post("/api/v1/customers/google/", payload, { headers });
        } catch (e1: any) {
          try {
            res = await api.post("/api/v1/auth/google/", payload, { headers });
          } catch (e2: any) {
            console.error("Backend Google Auth Error:", e1, e2);
            setError(
              e1?.response?.data?.error ||
                e2?.response?.data?.error ||
                "সার্ভারে গুগল লগইন রেকর্ড সংরক্ষণ করা সম্ভব হয়নি।"
            );
            setGoogleLoading(false);
            return;
          }
        }

        saveSessionAndClose(res.data, cleanEmail, payload.name, "");
      } catch (err: any) {
        const msg =
          err?.response?.data?.detail ||
          err?.response?.data?.error ||
          "Google authentication failed. Please try again.";
        setError(String(msg));
      } finally {
        setGoogleLoading(false);
      }
    },
    [saveSessionAndClose, subdomain]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash && window.location.hash.includes("access_token=")) {
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash);
      const accessToken = params.get("access_token");
      if (accessToken) {
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
        setGoogleLoading(true);
        fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
          headers: { Authorization: `Bearer ${accessToken}` },
        })
          .then((r) => r.json())
          .then((gData) => {
            if (gData?.email) {
              completeGoogleBackendAuth({
                email: gData.email,
                name: gData.name || `${gData.given_name || ""} ${gData.family_name || ""}`.trim(),
                first_name: gData.given_name,
                last_name: gData.family_name,
              });
            }
          })
          .catch(() => setGoogleLoading(false));
      }
    }
  }, [completeGoogleBackendAuth]);

  const handleGoogleSignInClick = () => {
    setError("");
    setGoogleLoading(true);

    const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

    if (!clientId) {
      setError(".env.local ফাইলে NEXT_PUBLIC_GOOGLE_CLIENT_ID পাওয়া যায়নি!");
      setGoogleLoading(false);
      return;
    }

    const isLocal =
      typeof window !== "undefined" &&
      (window.location.hostname.includes("localhost") || window.location.hostname === "127.0.0.1");

    const centralRedirectUri = isLocal
      ? `${window.location.protocol}//${window.location.host}`
      : "https://shop.xylotechsolution.com";

    const scope = encodeURIComponent("email profile openid");

    const oauthUrl =
      `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${encodeURIComponent(clientId)}&` +
      `redirect_uri=${encodeURIComponent(centralRedirectUri)}&` +
      `response_type=token&` +
      `scope=${scope}&` +
      `state=${encodeURIComponent(subdomain)}&` +
      `prompt=select_account`;

    const width = 500;
    const height = 620;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      oauthUrl,
      "GoogleAccountChooser",
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,status=no`
    );

    if (!popup) {
      window.location.href = oauthUrl;
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "XYLO_SAAS_GOOGLE_ACCESS_TOKEN" && event.data?.token) {
        window.removeEventListener("message", handleMessage);
        const accessToken = event.data.token;
        fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
          headers: { Authorization: `Bearer ${accessToken}` },
        })
          .then((r) => r.json())
          .then((gData) => {
            if (gData?.email) {
              completeGoogleBackendAuth({
                email: gData.email,
                name: gData.name || `${gData.given_name || ""} ${gData.family_name || ""}`.trim(),
                first_name: gData.given_name,
                last_name: gData.family_name,
              });
            } else {
              setError("Google profile data read kora jayni.");
              setGoogleLoading(false);
            }
          })
          .catch(() => {
            setError("Google userinfo fetch failed.");
            setGoogleLoading(false);
          });
      }
    };

    window.addEventListener("message", handleMessage);

    const timer = setInterval(() => {
      if (!popup || popup.closed) {
        clearInterval(timer);
        window.removeEventListener("message", handleMessage);
        setGoogleLoading(false);
      }
    }, 1000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");
    setLoading(true);

    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      setError("অনুগ্রহ করে আপনার সঠিক ইমেইল দিন।");
      setLoading(false);
      return;
    }

    try {
      const headers: Record<string, string> = {};
      if (subdomain) {
        headers["X-Tenant-Subdomain"] = subdomain;
      }

      if (tab === "login") {
        let res;
        try {
          res = await api.post(
            "/api/v1/customers/login/",
            { email: cleanEmail, password, subdomain },
            { headers }
          );
        } catch {
          res = await api.post(
            "/api/v1/auth/login/",
            { email: cleanEmail, password, subdomain },
            { headers }
          );
        }
        saveSessionAndClose(res.data, cleanEmail, firstName);
      } else {
        const fullName = `${firstName} ${lastName}`.trim() || cleanEmail.split("@")[0];
        let res;
        try {
          res = await api.post(
            "/api/v1/customers/register/",
            {
              email: cleanEmail,
              password,
              first_name: firstName.trim() || fullName.split(" ")[0],
              last_name: lastName.trim() || fullName.split(" ").slice(1).join(" "),
              name: fullName,
              phone: phone.trim(),
              subdomain,
            },
            { headers }
          );
        } catch {
          res = await api.post(
            "/api/v1/auth/register/",
            {
              email: cleanEmail,
              password,
              first_name: firstName.trim() || fullName.split(" ")[0],
              last_name: lastName.trim() || fullName.split(" ").slice(1).join(" "),
              name: fullName,
              phone: phone.trim(),
              role: "customer",
              subdomain,
            },
            { headers }
          );
        }
        saveSessionAndClose(res.data, cleanEmail, fullName, phone.trim());
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        (typeof err?.response?.data === "object"
          ? Object.values(err.response.data).flat().join(" ")
          : null) ||
        "অথেনটিকেশন ব্যর্থ হয়েছে। তথ্যগুলো যাচাই করে আবার চেষ্টা করুন।";
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-md rounded-3xl bg-white p-6 sm:p-8 shadow-2xl border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
        <button
          onClick={onClose}
          className="absolute right-5 top-5 rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition cursor-pointer"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="mb-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900">
            {tab === "login" ? "Welcome! Login" : "Create Account"}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {subdomain
              ? `Manage your orders and profile in the ${subdomain.toUpperCase()} store`
              : "Manage your orders and profile"}
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6 grid grid-cols-2 rounded-2xl bg-gray-100 p-1">
          <button
            type="button"
            onClick={() => {
              setTab("login");
              setError("");
            }}
            className={`rounded-xl py-2.5 text-sm font-semibold transition cursor-pointer ${
              tab === "login" ? "bg-white text-emerald-600 shadow-sm" : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => {
              setTab("register");
              setError("");
            }}
            className={`rounded-xl py-2.5 text-sm font-semibold transition cursor-pointer ${
              tab === "register" ? "bg-white text-emerald-600 shadow-sm" : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Register
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-xl bg-red-50 border border-red-200 p-3 text-xs font-medium text-red-600">
            {error}
          </div>
        )}

        {successMsg && (
          <div className="mb-4 flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs font-medium text-emerald-700">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            <span>{successMsg}</span>
          </div>
        )}

        {/* Google OAuth Button */}
        <div className="mb-5">
          <button
            type="button"
            onClick={handleGoogleSignInClick}
            disabled={googleLoading || loading}
            className="flex w-full items-center justify-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition disabled:opacity-60 cursor-pointer active:scale-98"
          >
            {googleLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
            ) : (
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
            )}
            <span>{tab === "login" ? "Continue with Google" : "Register with Google"}</span>
          </button>
        </div>

        <div className="relative mb-5 flex items-center justify-center">
          <div className="w-full border-t border-gray-200" />
          <span className="bg-white px-3 text-xs font-medium text-gray-400 uppercase">OR</span>
          <div className="w-full border-t border-gray-200" />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {tab === "register" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <User className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    required
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 py-3 pl-10 pr-3 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none"
                  />
                </div>
                <div className="relative">
                  <User className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 py-3 pl-10 pr-3 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="relative">
                <Phone className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400" />
                <input
                  type="tel"
                  placeholder="Phone Number (e.g. 017XXXXXXXX)"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 py-3 pl-10 pr-4 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none"
                />
              </div>
            </>
          )}

          <div className="relative">
            <Mail className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400" />
            <input
              type="email"
              required
              placeholder="Email Address (e.g. hasibsk@gmail.com)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-gray-200 py-3 pl-10 pr-4 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none"
            />
          </div>

          <div className="relative">
            <Lock className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400" />
            <input
              type="password"
              required
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-gray-200 py-3 pl-10 pr-4 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none"
            />
          </div>

          <button
            type="submit"
            disabled={loading || googleLoading}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 py-3.5 text-sm font-bold text-white shadow-lg shadow-emerald-600/20 hover:bg-emerald-700 transition disabled:opacity-60 cursor-pointer"
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                <span>{tab === "login" ? "Sign In" : "Create Account"}</span>
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}